import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Autosuggest from '@cloudscape-design/components/autosuggest';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import DatePicker from '@cloudscape-design/components/date-picker';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import Select, { type SelectProps } from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import TokenGroup from '@cloudscape-design/components/token-group';
import {
  ALL_FINANCE_ENTRY_TYPES,
  FinanceAccountId,
  FinanceEntryId,
  type FinanceAccount,
  type FinanceEntry,
  type FinanceEntryType,
} from '@etiquetachef/types';
import {
  createFinanceEntryCallable,
  updateFinanceEntryCallable,
} from '@/lib/callables';
import { toast } from '@/lib/notifications';
import { parseInputToCents } from '@/lib/money';
import { serverNow } from '@etiquetachef/ui-shared/online';

type Props = {
  open: boolean;
  mode: 'create' | 'edit';
  entry: FinanceEntry | null;
  accounts: FinanceAccount[];
  /** Tags já usadas em outros lançamentos, para autocomplete (#77). */
  knownTags: string[];
  onClose: () => void;
};

/** ms epoch → `AAAA-MM-DD` no fuso do navegador (valor do `DatePicker`). */
function msToDateInput(ms: number): string {
  const d = new Date(ms);
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${String(d.getFullYear())}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
/** `AAAA-MM-DD` → ms epoch ao meio-dia UTC (convenção do razão, #105); `null` se inválido. */
function dateInputToMs(value: string): number | null {
  const parts = value.split('-').map(Number);
  const [y, m, d] = parts;
  if (y === undefined || m === undefined || d === undefined) return null;
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return null;
  // Meio-dia UTC (não local!) — mesma convenção do import OCR; o fingerprint
  // do dedupe usa o ms cru, então meio-dia local nunca casaria com o extrato.
  return Date.UTC(y, m - 1, d, 12, 0, 0);
}
/** Hoje como `AAAA-MM-DD` local (default da data no modo criar). */
function todayInput(): string {
  return msToDateInput(serverNow());
}

/**
 * Modal de criar/editar um lançamento do Financeiro interno: conta, tipo IN/OUT, valor, data,
 * descrição e tags livres com autocomplete.
 *
 * Origem: #74 — 2026-06-21 20:47 +0200 (Financeiro PR B, lançamentos).
 *
 * Contrato: formulário reposto a cada abertura; com uma só conta ela já vem selecionada.
 * Validação local: conta, valor > 0 (`parseInputToCents`), descrição e data obrigatórios. Tags:
 * até 20, sem repetição, aceita "a, b, c" colado; sugestões = `knownTags` ainda não usadas
 * (#77). Submete `createFinanceEntry` / `updateFinanceEntry` (Functions, super-admin) — rules
 * de `finance_entries` têm `allow write: if false`; o servidor recusa editar lançamento de dia
 * anterior (`failed-precondition`), e aqui isso cai no `Alert` genérico. Sucesso → toast +
 * `onClose()`; quem chama recarrega a busca (`refreshNonce`).
 *
 * Usado por: `routes/FinanceEntriesPage.tsx` (web `apps/admin`, `/finance/entries`).
 *
 * Dados: `amountCents` em CENTAVOS; `date` em ms = MEIO-DIA UTC do dia civil
 * (`dateInputToMs`) — mesma convenção do import OCR, porque o fingerprint do dedupe usa o ms
 * cru e meio-dia local nunca casaria com o extrato; o servidor corta por dia no fuso da conta
 * (#105). Doc `finance_entries/{entryId}` (`FinanceEntrySchema`, `packages/types/src/finance.ts`).
 *
 * Dicas: `msToDateInput` daqui é cópia local da de `finance-analysis/financeAnalysisFormat.ts`
 * (mesmo corpo); na edição, `entry.amountCents / 100` é formatado direto, sem `money.ts`.
 *
 * Issues: #72 (épico), #74, #77 (tags), #105 (fuso).
 *
 * Histórico:
 *  - 2026-06-21 21:05 +0200 — (#74) tags como chips (`TokenGroup`).
 *  - 2026-06-21 22:45 +0200 — (sem issue, 1ae4b3218) Origens removidas; só tags.
 *  - 2026-06-21 22:59 +0200 — (#77) autocomplete de tags.
 *  - 2026-07-02 17:10 +0200 — (#105) data como meio-dia UTC.
 */
export function FinanceEntryEditorDialog({
  open,
  mode,
  entry,
  accounts,
  knownTags,
  onClose,
}: Props): React.JSX.Element | null {
  const { t } = useTranslation();
  const [accountId, setAccountId] = useState('');
  const [type, setType] = useState<FinanceEntryType>('OUT');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(todayInput());
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && entry) {
      setAccountId(entry.accountId);
      setType(entry.type);
      setAmount((entry.amountCents / 100).toFixed(2));
      setDate(msToDateInput(entry.date));
      setDescription(entry.description);
      setTags(entry.tags);
    } else {
      setAccountId(accounts.length === 1 && accounts[0] ? accounts[0].id : '');
      setType('OUT');
      setAmount('');
      setDate(todayInput());
      setDescription('');
      setTags([]);
    }
    setTagInput('');
    setError(null);
  }, [open, mode, entry, accounts]);

  const accountCurrency = useMemo(
    () => accounts.find((a) => a.id === accountId)?.currency ?? null,
    [accounts, accountId],
  );

  // Sugestões = tags já usadas que ainda não foram adicionadas a este
  // lançamento (#77 — padroniza a digitação para a Análise por tag).
  const tagSuggestions = useMemo(
    () => knownTags.filter((tg) => !tags.includes(tg)).map((tg) => ({ value: tg })),
    [knownTags, tags],
  );

  if (!open) return null;

  const accountOptions: SelectProps.Option[] = accounts.map((a) => ({
    value: a.id,
    label: a.name,
    description: a.currency,
  }));
  const typeOptions: SelectProps.Option[] = ALL_FINANCE_ENTRY_TYPES.map((ty) => ({
    value: ty,
    label: t(`admin.finance.entries.type.${ty}`),
  }));

  /** Adiciona tag(s) — aceita "a, b, c" colado; sem repetir; no máximo 20. */
  function addTag(override?: string): void {
    const raw = (override ?? tagInput).trim();
    if (raw === '') return;
    // Aceita várias de uma vez se colarem "a, b, c".
    const next = [...tags];
    for (const tg of raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0)) {
      if (!next.includes(tg) && next.length < 20) next.push(tg);
    }
    setTags(next);
    setTagInput('');
  }
  /** Remove a tag na posição `index`. */
  function removeTag(index: number): void {
    setTags((prev) => prev.filter((_, i) => i !== index));
  }

  /** Valida no cliente e chama `createFinanceEntry`/`updateFinanceEntry` (`manageFinance`). */
  async function submit(): Promise<void> {
    if (accountId === '') {
      setError(t('admin.finance.entries.errors.accountRequired'));
      return;
    }
    const cents = parseInputToCents(amount);
    if (cents === null || cents <= 0) {
      setError(t('admin.finance.entries.errors.amountInvalid'));
      return;
    }
    if (description.trim().length === 0) {
      setError(t('admin.finance.entries.errors.descriptionRequired'));
      return;
    }
    const ms = dateInputToMs(date);
    if (ms === null) {
      setError(t('admin.finance.entries.errors.dateRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'create') {
        await createFinanceEntryCallable({
          accountId: FinanceAccountId(accountId),
          type,
          amountCents: cents,
          date: ms,
          description: description.trim(),
          tags,
        });
        toast.success(t('admin.finance.entries.success.created'));
      } else if (entry) {
        await updateFinanceEntryCallable({
          entryId: FinanceEntryId(entry.id),
          accountId: FinanceAccountId(accountId),
          type,
          amountCents: cents,
          date: ms,
          description: description.trim(),
          tags,
        });
        toast.success(t('admin.finance.entries.success.updated'));
      }
      onClose();
    } catch (err) {
      console.error('FinanceEntryEditorDialog submit failed', err);
      setError(t('admin.finance.entries.errors.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible={open}
      onDismiss={onClose}
      header={t(mode === 'create' ? 'admin.finance.entries.create' : 'admin.finance.entries.edit')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              loading={submitting}
              onClick={(): void => {
                void submit();
              }}
            >
              {t('common.save')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {error !== null && <Alert type="error">{error}</Alert>}
        <FormField label={t('admin.finance.entries.form.account')}>
          <Select
            selectedOption={accountOptions.find((o) => o.value === accountId) ?? null}
            options={accountOptions}
            placeholder={t('admin.finance.entries.form.accountPlaceholder')}
            onChange={({ detail }): void => {
              setAccountId(detail.selectedOption.value ?? '');
            }}
          />
        </FormField>
        <FormField label={t('admin.finance.entries.form.type')}>
          <Select
            selectedOption={typeOptions.find((o) => o.value === type) ?? null}
            options={typeOptions}
            onChange={({ detail }): void => {
              setType(detail.selectedOption.value as FinanceEntryType);
            }}
          />
        </FormField>
        <FormField
          label={t('admin.finance.entries.form.amount')}
          description={accountCurrency ?? undefined}
        >
          <Input
            value={amount}
            inputMode="decimal"
            placeholder="0.00"
            onChange={({ detail }): void => {
              setAmount(detail.value);
            }}
          />
        </FormField>
        <FormField label={t('admin.finance.entries.form.date')}>
          <DatePicker
            value={date}
            onChange={({ detail }): void => {
              setDate(detail.value);
            }}
            placeholder="YYYY/MM/DD"
          />
        </FormField>
        <FormField label={t('admin.finance.entries.form.description')}>
          <Input
            value={description}
            placeholder={t('admin.finance.entries.form.descriptionPlaceholder')}
            onChange={({ detail }): void => {
              setDescription(detail.value);
            }}
          />
        </FormField>
        <FormField
          label={t('admin.finance.entries.form.tags')}
          description={t('admin.finance.entries.form.tagsHint')}
        >
          <SpaceBetween size="xs">
            <SpaceBetween size="xs" direction="horizontal">
              <Autosuggest
                value={tagInput}
                options={tagSuggestions}
                enteredTextLabel={(v): string => v}
                placeholder={t('admin.finance.entries.form.tagsPlaceholder')}
                empty={t('admin.finance.entries.form.tagsNoMatch')}
                onChange={({ detail }): void => {
                  setTagInput(detail.value);
                }}
                onSelect={({ detail }): void => {
                  addTag(detail.value);
                }}
                onKeyDown={({ detail }): void => {
                  if (detail.key === 'Enter') addTag();
                }}
              />
              <Button
                onClick={(): void => {
                  addTag();
                }}
              >
                {t('common.add')}
              </Button>
            </SpaceBetween>
            {tags.length > 0 && (
              <TokenGroup
                items={tags.map((tg) => ({ label: tg }))}
                onDismiss={({ detail }): void => {
                  removeTag(detail.itemIndex);
                }}
              />
            )}
          </SpaceBetween>
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}
